import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { track, getAttribution } from '../lib/analytics';
import { Camera, X, ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';

export const QUOTE_SERVICES = [
  { value: 'garage', label: 'Garage Floor Coating' },
  { value: 'flake', label: 'Flake System' },
  { value: 'metallic', label: 'Metallic Epoxy' },
  { value: 'quartz', label: 'Quartz System' },
  { value: 'pool', label: 'Pool Deck Resurfacing' },
  { value: 'commercial', label: 'Commercial Epoxy' },
  { value: 'industrial', label: 'Industrial Epoxy' },
  { value: 'urethane', label: 'Urethane Cement Flooring' },
  { value: 'polishing', label: 'Concrete Polishing' },
  { value: 'concrete', label: 'Concrete Repair' },
  { value: 'driveway', label: 'Driveway Resurfacing' },
  { value: 'waterproofing', label: 'Waterproofing' },
  { value: 'government', label: 'Government / Public Sector' },
  { value: 'other', label: 'Other / Not Sure' },
];

const MAX_PHOTOS = 5;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 1280;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Invalid image')); };
    img.src = url;
  });

const MultiStepQuoteForm = ({ defaultService = '' }) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: defaultService, location: '', sqft: '', message: '',
    property_type: '', existing_coating: '', damage: '', timeframe: '', zip_code: '', preferred_contact: '', website: ''
  });

  useEffect(() => {
    const param = new URLSearchParams(location.search).get('service');
    if (param && QUOTE_SERVICES.some((s) => s.value === param)) {
      setFormData((prev) => ({ ...prev, service: param }));
    }
  }, [location.search]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const available = MAX_PHOTOS - photos.length;
    if (available <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    for (const file of files.slice(0, available)) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const dataUrl = await compressImage(file);
        setPhotos((prev) => prev.length < MAX_PHOTOS
          ? [...prev, { name: file.name.replace(/\.[^.]+$/, '') + '.jpg', dataUrl }]
          : prev);
      } catch {
        toast.error(`Could not read ${file.name}`);
      }
    }
  };

  const removePhoto = (idx) => setPhotos(photos.filter((_, i) => i !== idx));

  const goNext = () => {
    if (step === 1 && !formData.service) {
      toast.error('Please select a service');
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Please complete all required fields');
      return;
    }
    setSubmitting(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const photoIds = [];
      for (let i = 0; i < photos.length; i++) {
        setProgressText(`Uploading photo ${i + 1}/${photos.length}...`);
        try {
          const res = await fetch(`${BACKEND_URL}/api/quotes/photos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              filename: photos[i].name,
              content_type: 'image/jpeg',
              content_base64: photos[i].dataUrl.split(',')[1],
            }),
          });
          if (res.ok) {
            const data = await res.json();
            photoIds.push(data.id);
          }
        } catch (uploadErr) {
          console.error('Photo upload failed:', uploadErr);
        }
      }
      if (photos.length > 0 && photoIds.length < photos.length) {
        toast.warning(`${photos.length - photoIds.length} photo(s) could not be uploaded — your request will still be sent.`);
      }
      setProgressText('Sending request...');
      const response = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, photo_ids: photoIds, ...getAttribution() }),
      });
      if (response.ok) {
        track('quote_form_submitted', { service: formData.service, property_type: formData.property_type, photos: photoIds.length });
        toast.success("Quote request submitted! We'll contact you within 24 hours.");
        setFormData({ name: '', email: '', phone: '', service: '', location: '', sqft: '', message: '', property_type: '', existing_coating: '', damage: '', timeframe: '', zip_code: '', preferred_contact: '', website: '' });
        setPhotos([]);
        setStep(1);
      } else {
        toast.error('Failed to submit request. Please try again or call us directly.');
      }
    } catch (err) {
      console.error('Quote submit error:', err);
      toast.error('Failed to submit request. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
      setProgressText('');
    }
  };

  const steps = [
    { n: 1, label: 'Project' },
    { n: 2, label: 'Photos' },
    { n: 3, label: 'Contact' },
  ];

  return (
    <Card className="border-2 border-gray-200 shadow-xl" data-testid="multistep-quote-form">
      <CardContent className="p-8">
        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-8" data-testid="quote-form-progress">
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  step > s.n ? 'bg-green-500 text-white' : step === s.n ? 'bg-brand-gold text-brand-black' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.n ? <Check className="w-5 h-5" /> : s.n}
                </div>
                <span className={`text-xs mt-1 font-medium ${step >= s.n ? 'text-gray-900' : 'text-gray-400'}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-16 sm:w-24 h-1 mx-2 mb-4 rounded ${step > s.n ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit} method="post" className="space-y-6">
          <input type="text" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" placeholder="Your website" className="absolute opacity-0 -z-10 h-0 w-0 pointer-events-none" data-testid="quote-form-honeypot" />
          {step === 1 && (
            <div className="space-y-6" data-testid="quote-step-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property type</label>
                <div className="grid grid-cols-2 gap-3" data-testid="quote-property-type">
                  {['Residential', 'Commercial'].map((pt) => (
                    <button
                      key={pt}
                      type="button"
                      onClick={() => setFormData({ ...formData, property_type: pt })}
                      data-testid={`quote-property-${pt.toLowerCase()}`}
                      className={`border-2 rounded-md py-3 font-semibold transition-colors ${
                        formData.property_type === pt
                          ? 'border-brand-gold bg-brand-gold/10 text-gray-900'
                          : 'border-gray-300 text-gray-600 hover:border-brand-gold/50'
                      }`}
                    >
                      {pt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="msq-service" className="block text-sm font-medium text-gray-700 mb-2">What service do you need? *</label>
                <select
                  id="msq-service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  data-testid="quote-service-select"
                  className="w-full border border-gray-300 rounded-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                >
                  <option value="">Select a service</option>
                  {QUOTE_SERVICES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approximate size (sq ft)</label>
                <Input
                  name="sqft"
                  value={formData.sqft}
                  onChange={handleChange}
                  placeholder="e.g. 500 sq ft (2-car garage) — approximate is fine"
                  data-testid="quote-sqft-input"
                  className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold"
                />
              </div>
              <Button type="button" onClick={goNext} size="lg" className="w-full bg-brand-gold hover:bg-brand-gold-dark text-brand-black text-lg py-6" data-testid="quote-step1-next-btn">
                Next: Add Photos <ChevronRight className="ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="quote-step-2">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Existing coating on the floor?</label>
                  <select
                    name="existing_coating"
                    value={formData.existing_coating}
                    onChange={handleChange}
                    data-testid="quote-coating-select"
                    className="w-full border border-gray-300 rounded-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  >
                    <option value="">Select (optional)</option>
                    <option value="Bare concrete">Bare concrete — no coating</option>
                    <option value="Paint">Paint</option>
                    <option value="Old epoxy">Old epoxy / DIY kit</option>
                    <option value="Tile">Tile</option>
                    <option value="Sealer">Sealer</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cracks or damage?</label>
                  <select
                    name="damage"
                    value={formData.damage}
                    onChange={handleChange}
                    data-testid="quote-damage-select"
                    className="w-full border border-gray-300 rounded-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  >
                    <option value="">Select (optional)</option>
                    <option value="No visible damage">No visible damage</option>
                    <option value="Minor cracks">Minor cracks</option>
                    <option value="Large cracks / spalling">Large cracks / spalling</option>
                    <option value="Moisture issues">Moisture issues</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
              </div>
              <div className="text-center">
                <label className="block text-lg font-semibold text-gray-900 mb-1">Add photos of your floor (optional)</label>
                <p className="text-sm text-gray-500 mb-4">Photos help us give you a faster, more accurate estimate. Up to {MAX_PHOTOS}.</p>
                <label htmlFor="msq-photos" className="cursor-pointer inline-flex flex-col items-center justify-center gap-2 border-2 border-dashed border-brand-gold/50 rounded-lg px-10 py-8 hover:bg-brand-gold/5 transition-colors w-full" data-testid="quote-photo-dropzone">
                  <Camera className="w-10 h-10 text-brand-gold" />
                  <span className="font-semibold text-gray-700">Tap to take or upload photos</span>
                  <span className="text-xs text-gray-400">JPG, PNG, HEIC — compressed automatically</span>
                </label>
                <input
                  id="msq-photos"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="quote-photo-input"
                />
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="quote-photo-previews">
                  {photos.map((p, idx) => (
                    <div key={idx} className="relative group">
                      <img src={p.dataUrl} alt={`Upload preview ${idx + 1}`} className="w-full h-20 object-cover rounded-md border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        aria-label={`Remove photo ${idx + 1}`}
                        data-testid={`quote-photo-remove-${idx}`}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => setStep(1)} size="lg" className="flex-1 border-gray-300 py-6" data-testid="quote-step2-back-btn">
                  <ChevronLeft className="mr-1" /> Back
                </Button>
                <Button type="button" onClick={goNext} size="lg" className="flex-1 bg-brand-gold hover:bg-brand-gold-dark text-brand-black py-6" data-testid="quote-step2-next-btn">
                  {photos.length > 0 ? 'Next' : 'Skip'} <ChevronRight className="ml-1" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="quote-step-3">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required data-testid="quote-name-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required data-testid="quote-email-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(239) 555-0123" required data-testid="quote-phone-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                  <Input name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="34117" data-testid="quote-zip-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City / Location</label>
                  <Input name="location" value={formData.location} onChange={handleChange} placeholder="Naples, Fort Myers..." data-testid="quote-location-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project timeframe</label>
                  <select
                    name="timeframe"
                    value={formData.timeframe}
                    onChange={handleChange}
                    data-testid="quote-timeframe-select"
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  >
                    <option value="">Select (optional)</option>
                    <option value="As soon as possible">As soon as possible</option>
                    <option value="Within 2 weeks">Within 2 weeks</option>
                    <option value="Within 1-2 months">Within 1-2 months</option>
                    <option value="Just planning">Just planning / budgeting</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred contact method</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quote-contact-method">
                  {['Phone call', 'Text', 'WhatsApp', 'Email'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFormData({ ...formData, preferred_contact: m })}
                      data-testid={`quote-contact-${m.toLowerCase().replace(' ', '-')}`}
                      className={`border-2 rounded-md py-2 text-sm font-semibold transition-colors ${
                        formData.preferred_contact === m
                          ? 'border-brand-gold bg-brand-gold/10 text-gray-900'
                          : 'border-gray-300 text-gray-600 hover:border-brand-gold/50'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Details *</label>
                <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project: current floor condition, timeline, etc." rows={4} required data-testid="quote-message-input" className="border-gray-300 focus:border-brand-gold focus:ring-brand-gold" />
              </div>
              {photos.length > 0 && (
                <p className="text-sm text-gray-500 flex items-center gap-2"><Camera className="w-4 h-4 text-brand-gold" /> {photos.length} photo{photos.length > 1 ? 's' : ''} will be sent with your request</p>
              )}
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => setStep(2)} size="lg" className="border-gray-300 py-6" disabled={submitting} data-testid="quote-step3-back-btn">
                  <ChevronLeft className="mr-1" /> Back
                </Button>
                <Button type="submit" size="lg" className="flex-1 bg-brand-gold hover:bg-brand-gold-dark text-brand-black text-lg py-6" disabled={submitting} data-testid="quote-submit-btn">
                  {submitting ? (<><Loader2 className="mr-2 animate-spin" /> {progressText || 'Sending...'}</>) : (<>Request Free Quote <ChevronRight className="ml-2" /></>)}
                </Button>
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default MultiStepQuoteForm;
